/**
 * Task Query Hooks
 * TASK-359: TaskListPage & Dashboard Integration
 *
 * React Query hooks for task data fetching and caching.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { taskService, type CompleteTaskOptions } from '../services/taskService';
import {
  getFailedLifeEventActions,
  retryFailedLifeEventAction,
} from '../services/knowledgeGraphService';
import {
  TaskCreateRequest,
  TaskUpdateRequest,
  TaskListFilters,
} from '../types/tasks';

// Query keys
export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (filters: TaskListFilters) => [...taskKeys.lists(), filters] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
  stats: () => [...taskKeys.all, 'stats'] as const,
  overdue: () => [...taskKeys.all, 'overdue'] as const,
  dueToday: () => [...taskKeys.all, 'dueToday'] as const,
  highPriority: () => [...taskKeys.all, 'highPriority'] as const,
  failedLifeEventActions: () => [...taskKeys.all, 'failedLifeEventActions'] as const,
};

/**
 * Hook to fetch tasks with filters
 */
export function useTasks(filters?: TaskListFilters) {
  return useQuery({
    queryKey: taskKeys.list(filters || {}),
    queryFn: () => taskService.fetchTasks(filters),
    staleTime: 1000 * 60, // 1 minute
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch a single task
 */
export function useTask(taskId: string) {
  return useQuery({
    queryKey: taskKeys.detail(taskId),
    queryFn: () => taskService.getTask(taskId),
    enabled: !!taskId,
    staleTime: 1000 * 30, // 30 seconds
  });
}

/**
 * Hook to fetch dashboard statistics
 */
export function useTaskDashboardStats(assigneeUserId?: string) {
  return useQuery({
    queryKey: [...taskKeys.stats(), assigneeUserId],
    queryFn: () => taskService.getDashboardStats(assigneeUserId),
    staleTime: 1000 * 60, // 1 minute
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch overdue tasks
 */
export function useOverdueTasks(limit = 10) {
  return useQuery({
    queryKey: [...taskKeys.overdue(), limit],
    queryFn: () => taskService.fetchOverdueTasks(limit),
    staleTime: 1000 * 60, // 1 minute
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch tasks due today
 */
export function useTasksDueToday() {
  return useQuery({
    queryKey: taskKeys.dueToday(),
    queryFn: () => taskService.fetchTasksDueToday(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch high priority pending tasks
 */
export function useHighPriorityTasks(limit = 10) {
  return useQuery({
    queryKey: [...taskKeys.highPriority(), limit],
    queryFn: () => taskService.fetchHighPriorityTasks(limit),
    staleTime: 1000 * 60, // 1 minute
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch tasks for a specific client
 */
export function useClientTasks(clientId: string, limit = 50) {
  return useQuery({
    queryKey: [...taskKeys.lists(), 'client', clientId, limit],
    queryFn: () => taskService.fetchClientTasks(clientId, limit),
    enabled: !!clientId,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Mutation hook to create a task
 */
export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (task: TaskCreateRequest) => taskService.createTask(task),
    onSuccess: () => {
      // Invalidate all task lists
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

/**
 * Mutation hook to update a task
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, updates }: { taskId: string; updates: TaskUpdateRequest }) =>
      taskService.updateTask(taskId, updates),
    onSuccess: (data, variables) => {
      // Update the specific task in cache
      queryClient.setQueryData(taskKeys.detail(variables.taskId), data);
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskKeys.stats() });
    },
  });
}

/**
 * Mutation hook to complete a task
 */
export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    // TASK-758: a plain id is an ordinary sign-off; the object form carries an exception
    // reason for work that could not be opened.
    // TASK-922: and, for a task bound to a signal subject, the outcome the server requires.
    mutationFn: (v: string | ({ taskId: string } & CompleteTaskOptions)) => {
      if (typeof v === 'string') return taskService.completeTask(v);
      const { taskId, ...options } = v;
      return taskService.completeTask(taskId, options);
    },
    onSuccess: (data, variables) => {
      const taskId = typeof variables === 'string' ? variables : variables.taskId;
      // Update the specific task in cache
      queryClient.setQueryData(taskKeys.detail(taskId), data);
      // Invalidate lists and stats
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taskKeys.stats() });
      queryClient.invalidateQueries({ queryKey: taskKeys.overdue() });
    },
  });
}

/**
 * Mutation hook to snooze a task
 */
export function useSnoozeTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, snoozeUntil }: { taskId: string; snoozeUntil: string }) =>
      taskService.snoozeTask(taskId, snoozeUntil),
    onSuccess: (data, variables) => {
      // Update the specific task in cache
      queryClient.setQueryData(taskKeys.detail(variables.taskId), data);
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    },
  });
}

/**
 * Hook to fetch unresolved failed life-event → action proposals (TASK-576 F3).
 * These are dropped mandatory compliance actions awaiting a Retry on the Today surface.
 */
export function useFailedLifeEventActions() {
  return useQuery({
    queryKey: taskKeys.failedLifeEventActions(),
    queryFn: () => getFailedLifeEventActions(),
    staleTime: 1000 * 60, // 1 minute
    refetchOnWindowFocus: true,
  });
}

/**
 * Mutation hook to retry a failed life-event action. A successful retry creates the
 * missing task(s), so invalidate both the failures list and the task lists.
 */
export function useRetryFailedLifeEventAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => retryFailedLifeEventAction(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.failedLifeEventActions() });
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

/**
 * Mutation hook to delete a task
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => taskService.deleteTask(taskId),
    onSuccess: () => {
      // Invalidate all task-related queries
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}
